import { isFieldTableTicket } from "./domain";
import { ONSITE_DOOR_PRICES, packageFromTicketType, type PricePackage } from "./settle";
import type { FeePayoutRecord, Session, Ticket } from "./types";

export const DENOMINATIONS = [1000, 500, 200, 100, 50, 10, 5, 1] as const;

export { ONSITE_DOOR_PRICES } from "./settle";

export type Denomination = (typeof DENOMINATIONS)[number];
export type CashCheckpoint = "opening" | "afternoon" | "evening";

export type CashCounts = Record<Denomination, number>;

export interface CashCountRecord {
  checkpoint: CashCheckpoint;
  counts: CashCounts;
  balance: number;
  income: number;
  feeSnapshot: number;
  feeLocked: boolean;
  updatedAt?: string;
  device?: string;
}

export function emptyCounts(): CashCounts {
  return { 1000: 0, 500: 0, 200: 0, 100: 0, 50: 0, 10: 0, 5: 0, 1: 0 };
}

export function emptyCashRecord(checkpoint: CashCheckpoint): CashCountRecord {
  return {
    checkpoint,
    counts: emptyCounts(),
    balance: 0,
    income: 0,
    feeSnapshot: 0,
    feeLocked: false,
  };
}

export function normalizeCounts(raw: Partial<Record<string, number>> | null | undefined): CashCounts {
  const counts = emptyCounts();
  for (const denomination of DENOMINATIONS) {
    const value = Number(raw?.[String(denomination)] ?? 0);
    counts[denomination] = Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;
  }
  return counts;
}

export function computeBalance(counts: CashCounts): number {
  return DENOMINATIONS.reduce((sum, denomination) => sum + denomination * (counts[denomination] || 0), 0);
}

export function computeBalanceDelta(currentBalance: number, previousBalance: number): number {
  return currentBalance - previousBalance;
}

/** variance = balanceDelta - (income + feeSnapshot); feeSnapshot is negative. */
export function computeVariance(input: {
  balanceDelta: number;
  income: number;
  feeSnapshot: number;
}): number {
  return input.balanceDelta - (input.income + input.feeSnapshot);
}

function packageFromTicket(ticket: Ticket): PricePackage {
  if (ticket.opsStatus === "onsite_both") return "both";
  if (ticket.opsStatus === "onsite_evening") return "evening";
  if (ticket.opsStatus === "onsite_afternoon") return "afternoon";
  return packageFromTicketType(ticket.ticketType || "");
}

/** Per-ticket onsite list price for door cash (listPrice if set, else package table). */
export function onsiteDoorListPrice(ticket: Ticket): number {
  if (ticket.listPrice != null && Number.isFinite(Number(ticket.listPrice))) {
    return Math.round(Number(ticket.listPrice));
  }
  return ONSITE_DOOR_PRICES[packageFromTicket(ticket)];
}

function hasCheckedInUnit(ticket: Ticket): boolean {
  if ((ticket.units || []).some((unit) => unit.checkedIn)) return true;
  return (
    Number(ticket.checkIns?.afternoon?.count || 0) > 0 ||
    Number(ticket.checkIns?.evening?.count || 0) > 0
  );
}

/**
 * Ticket uses prior credit / 延用（7/11 額度或表單 priorCredit）。
 * Door topup/refund eligibility also requires settlePaidAtDoor (excludes bank 匯款).
 */
export function isPriorCreditTicket(ticket: Ticket): boolean {
  if (ticket.creditSourceId) return true;
  return (Number(ticket.priorCredit) || 0) > 0;
}

/**
 * Ticket's session bucket for door-income attribution. Both-session (通票)
 * tickets are attributed to "afternoon": 8/16 data showed no 通票 holder
 * ever checked in evening-only (they always show up at noon first), so
 * splitting the lump-sum price would only add complexity without a real
 * case to support it. Revisit if that assumption stops holding.
 */
function sessionBucket(ticket: Ticket): Session {
  const sessions = ticket.sessions || [];
  return sessions.includes("evening") && !sessions.includes("afternoon") ? "evening" : "afternoon";
}

/**
 * 錢箱自動收入（可為負；不 clamp）：某場次的
 * 現場票收款 + 現場補款 − 現場退款
 *
 * - 現場票收款：source=field 匿名／公關表 onsite（非 PR），至少一單位已核 → 票價一次
 * - 現場補／退：settlePaidAtDoor + topup_paid／refunded（核票卡按鈕）；
 *   匯款確認 topup_paid 不計入。包含延用結算與預售票現場升級。
 * - 每張票依 sessionBucket 歸入單一場次；通票一律算午場。
 */
export function computeDoorIncome(tickets: Ticket[], session: Session): number {
  let fieldCheckin = 0;
  let doorTopup = 0;
  let doorRefund = 0;

  for (const ticket of tickets) {
    if (sessionBucket(ticket) !== session) continue;

    if (
      isFieldTableTicket(ticket) &&
      ticket.kind === "onsite" &&
      hasCheckedInUnit(ticket)
    ) {
      fieldCheckin += onsiteDoorListPrice(ticket);
    }

    if (!ticket.settlePaidAtDoor) continue;
    const amount = Math.abs(Number(ticket.settleDelta) || 0);
    if (ticket.settleStatus === "topup_paid") doorTopup += amount;
    else if (ticket.settleStatus === "refunded") doorRefund += amount;
  }

  return fieldCheckin + doorTopup - doorRefund;
}

export function buildFeeSnapshot(payouts: FeePayoutRecord[], session: Session): number {
  const paid = payouts
    .filter((item) => item.session === session)
    .reduce((sum, item) => sum + Math.max(0, Number(item.amountPaid) || 0), 0);
  return paid === 0 ? 0 : -paid;
}

export function previousCheckpoint(checkpoint: CashCheckpoint): CashCheckpoint | null {
  if (checkpoint === "afternoon") return "opening";
  if (checkpoint === "evening") return "afternoon";
  return null;
}

export function normalizeCashRecord(
  raw: Partial<CashCountRecord> & { checkpoint: CashCheckpoint },
): CashCountRecord {
  const counts = normalizeCounts(raw.counts);
  return {
    checkpoint: raw.checkpoint,
    counts,
    balance: computeBalance(counts),
    // Door income may be negative when 現場退款 dominate; do not clamp.
    income: Number(raw.income) || 0,
    feeSnapshot: Number(raw.feeSnapshot) || 0,
    feeLocked: Boolean(raw.feeLocked),
    updatedAt: raw.updatedAt,
    device: raw.device,
  };
}
